import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { EmbedDevice } from 'tag/ts/tag/model/type/embed/EmbedDevice';
import { EmbedType } from 'tag/ts/tag/model/type/embed/EmbedType';
import { ComcastCapabilitiesExtension } from 'tag/ts/tag/player/adapters/avia/extensions/capabilities/html5/ComcastCapabilitiesExtension';
import { LgCapabilitiesExtension } from 'tag/ts/tag/player/adapters/avia/extensions/capabilities/html5/LgCapabilitiesExtension';
import { SamsungCapabilitiesExtension } from 'tag/ts/tag/player/adapters/avia/extensions/capabilities/html5/SamsungCapabilitiesExtension';
import { XboxCapabilitiesExtension } from 'tag/ts/tag/player/adapters/avia/extensions/capabilities/html5/XboxCapabilitiesExtension';
import { HdcpCapabilitiesExtension } from 'tag/ts/tag/player/adapters/avia/extensions/capabilities/utility/HdcpCapabilitiesExtension';
export class AviaExtensionsManager extends AbstractTagClass {
    get #allExtensions() {
        return [
            ...this.#capabilitiesExtensions
        ];
    }
    get #capabilitiesExtensions() {
        return [
            new LgCapabilitiesExtension({ tag: this?.tag }),
            new XboxCapabilitiesExtension({ tag: this?.tag }),
            new SamsungCapabilitiesExtension({ tag: this?.tag }),
            new ComcastCapabilitiesExtension({ tag: this?.tag }),
            new HdcpCapabilitiesExtension({ tag: this?.tag })
        ];
    }
    get #debugging() {
        if (!this.runtimeVO?.debug?.on) {
            return {};
        }
        return {
            forceAllExtensionsToLoad: false
        };
    }
    constructor(argument) {
        super({
            ...argument,
            name: 'Avia Extensions Manager'
        });
    }
    loadCapabilitiesExtensions() {
        return this.#registerExtensions(this.#capabilitiesExtensions);
    }
    loadAllExtensions() {
        return this.#registerExtensions(this.#allExtensions);
    }
    #registerExtensions(roster) {
        const extensionPromises = [];
        const failures = [];
        const criticalFailures = [];
        roster
            .filter((extension) => {
            const extensionShouldLoad = extension.shouldBeUsed() || this.#debugging?.forceAllExtensionsToLoad;
            if (!extensionShouldLoad) {
                this?.logger?.log('Extension', extension.name, ' will not be loaded');
            }
            return extensionShouldLoad;
        })
            .forEach((extension) => {
            extensionPromises.push({
                extension: extension,
                promise: extension.register().catch((error) => {
                    const isCritical = this.#isCriticalExtension(extension.name);
                    failures.push({
                        extension: extension.name,
                        error: error,
                        critical: isCritical
                    });
                    if (isCritical) {
                        criticalFailures.push(extension.name);
                        this?.logger?.error('CRITICAL extension registration failed:', extension.name, error);
                    }
                    else {
                        this?.logger?.warn('Optional extension registration failed:', extension.name, error);
                    }
                    return Promise.resolve();
                })
            });
        });
        this?.logger?.log('registerExtensions', 'extensionsLoading', extensionPromises.length);
        return Promise.allSettled(extensionPromises.map((ep) => ep.promise)).then((results) => {
            const successful = results.filter((r) => r.status === 'fulfilled').length;
            if (failures.length > 0) {
                this?.logger?.warn('Extension registration summary:', {
                    successful: successful,
                    failed: failures.length,
                    criticalFailures: criticalFailures.length,
                    failures: failures
                });
            }
            else {
                this?.logger?.log('Extension loading complete. All extensions loaded successfully.');
            }
            if (criticalFailures.length > 0) {
                this?.logger?.error(`Critical extensions failed to register: ${criticalFailures.join(', ')}`);
                return false;
            }
            return true;
        });
    }
    #isCriticalExtension = (extensionName) => {
        const currentDevice = this.tag.params?.SYSTEM?.device;
        if (extensionName === 'HdcpCapabilitiesExtension') {
            const isHDCPDevice = [
                EmbedDevice.XBOX_ONE,
                EmbedDevice.PS4,
                EmbedDevice.PS5,
                EmbedDevice.COMCAST,
                EmbedDevice.COX,
                EmbedDevice.VIDAA,
                EmbedDevice.DESKTOP
            ].includes(currentDevice);
            return isHDCPDevice;
        }
        switch (extensionName) {
            case 'XboxCapabilitiesExtension':
                return currentDevice === EmbedDevice.XBOX_ONE;
            case 'ComcastCapabilitiesExtension':
                return currentDevice === EmbedDevice.COMCAST;
            case 'SamsungCapabilitiesExtension':
            case 'LgCapabilitiesExtension':
                return this.params?.SYSTEM?.embedType === EmbedType.HTML5;
            default:
                return false;
        }
    };
}
